// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Loader2 } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCampaignsByIds } from "@/hooks/api/use-campaigns";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import { useCampaignSelection } from "@/hooks/use-campaign-selection";
import { getCampaignColumns } from "@/pages/campaigns/campaign-list/campaign-columns";
import type { UnifiedTableColumn } from "@/types/table-column";
import { BenchmarkDataTable } from "./benchmark-data-table";

type BenchmarkCohortMode = "dynamic" | "static";

interface BenchmarkConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveConfig: (
    filters: Record<string, unknown>,
    campaignCount: number,
    excludeCurrentCampaign: boolean,
    mode: BenchmarkCohortMode,
    selectedCampaignIds?: string[],
  ) => Promise<void>;
  initialFilters?: Record<string, unknown>;
  initialExcludeCurrentCampaign?: boolean;
  initialMode?: BenchmarkCohortMode;
  initialSelectedCampaignIds?: string[];
}

// Component to display the campaign count badge
interface CohortCountBadgeProps {
  effectiveCount: number;
  cohortMode: BenchmarkCohortMode;
}

const CohortCountBadge = memo(function CohortCountBadge({
  effectiveCount,
  cohortMode,
}: CohortCountBadgeProps) {
  // Determine badge color based on count
  let badgeClass = "bg-muted text-muted-foreground";
  if (effectiveCount > 0 && effectiveCount <= 500) {
    badgeClass = "bg-primary/15 text-primary font-medium";
  } else if (effectiveCount > 500) {
    badgeClass = "bg-destructive/15 text-destructive font-medium";
  }

  // Customize message based on cohort mode
  const modeMessage =
    cohortMode === "static"
      ? "selected for benchmark cohort"
      : "included in benchmark cohort";

  return (
    <div
      className={`rounded-md px-3 py-1.5 text-sm flex items-center ${badgeClass}`}
    >
      <span className="font-semibold mr-1">{effectiveCount}</span>
      <span>
        {effectiveCount === 1 ? "campaign" : "campaigns"} {modeMessage}
      </span>
      {effectiveCount > 500 && (
        <span className="text-destructive ml-2 text-xs">
          (please filter to 500 or fewer to save)
        </span>
      )}
    </div>
  );
});

export function BenchmarkConfigModal({
  open,
  onOpenChange,
  onSaveConfig,
  initialFilters,
  initialExcludeCurrentCampaign = true,
  initialMode = "static",
  initialSelectedCampaignIds = [],
}: BenchmarkConfigModalProps) {
  // State variables
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [campaignCount, setCampaignCount] = useState(0);
  const [tableFilters, setTableFilters] = useState<Record<string, unknown>>({});
  const [excludeCurrentCampaign, setExcludeCurrentCampaign] = useState(
    initialExcludeCurrentCampaign,
  );
  const [cohortMode, setCohortMode] =
    useState<BenchmarkCohortMode>(initialMode);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>(
    initialSelectedCampaignIds,
  );
  const [currentCampaignMatches, setCurrentCampaignMatches] = useState(false);

  // Refs
  const isSaving = useRef(false);

  // Hooks
  const { selectedCampaignId } = useCampaignSelection();
  const { data: currentCampaign } = useCampaignsByIds(
    selectedCampaignId ? [selectedCampaignId] : undefined,
  );
  const { data: connections } = useExternalConnectionLinks();
  const { data: fieldDefs } = useFieldDefinitions("campaigns");

  const filterOptions = useMemo(() => {
    if (!fieldDefs) return undefined;

    const getEnum = (id: string) =>
      fieldDefs.find((f) => f.id === id && f.has_enum_values)?.enum_values ||
      [];

    return {
      type: getEnum("type"),
      source_record_type: getEnum("source_record_type"),
    } as const;
  }, [fieldDefs]);

  // Get columns without any metrics and disable the campaign name link
  const columns = getCampaignColumns(
    filterOptions,
    connections,
    false, // isLoadingConnections
    [], // no metrics selected
  )
    .filter((column) => !("metricInfo" in column))
    .map((column) => {
      // Create a copy of the column
      const newColumn = { ...column };

      // For the 'name' column, override the render function to display text without a link
      if (column.key === "name") {
        newColumn.render = (campaign) => (
          <span className="px-2 py-1 block">{campaign.name}</span>
        );
      }

      return newColumn;
    });

  // Handle saving the benchmark configuration
  const handleSave = async () => {
    if (effectiveCount > 500) return;

    setIsLoading(true);
    isSaving.current = true;

    try {
      // Capture current filters before saving
      const filtersToSave = { ...tableFilters };

      // Save the benchmark config with exclude flag, mode and selected campaigns
      await onSaveConfig(
        filtersToSave,
        effectiveCount, // Use our already calculated effective count
        excludeCurrentCampaign,
        cohortMode,
        cohortMode === "static" ? selectedCampaignIds : undefined,
      );

      // Show success toast and close modal after successful save
      toast.success("Benchmark configuration saved successfully");
      onOpenChange(false);
    } catch (error) {
      // Log the error and show user feedback
      console.error("Failed to save benchmark configuration:", error);
      toast.error("Failed to save benchmark configuration. Please try again.");
    } finally {
      setIsLoading(false);
      isSaving.current = false;
    }
  };

  // Handle clearing all filters and resetting benchmark state
  const handleClearAll = async () => {
    setIsClearing(true);
    isSaving.current = true; // Prevent concurrent saves/closes

    try {
      // Pass empty configuration to completely reset the benchmark state
      await onSaveConfig({}, 0, true, "static", []);

      // Show success toast and close the modal after clearing
      toast.success("Benchmark configuration has been reset");
      onOpenChange(false);
    } catch (error) {
      // Log the error and show user feedback
      console.error("Failed to reset benchmark configuration:", error);
      toast.error("Failed to reset benchmark configuration. Please try again.");
    } finally {
      // Reset state flags
      setIsClearing(false);
      isSaving.current = false;
    }
  };

  // Calculate the effective campaign count considering exclude toggle and mode
  const effectiveCount = useMemo(() => {
    // For static mode, simply use the selectedCampaignIds length
    // (exclude toggle not relevant for static mode)
    if (cohortMode === "static") {
      return selectedCampaignIds.length;
    }

    // For dynamic mode, use the campaign count from the API and apply exclude logic
    let count = campaignCount;

    // Only subtract if all conditions are met:
    // 1. Exclude toggle is on
    // 2. Campaign count is positive
    // 3. Current campaign matches the filters (determined via callback)
    if (excludeCurrentCampaign && count > 0 && currentCampaignMatches) {
      count = Math.max(0, count - 1);
    }

    return count;
  }, [
    cohortMode,
    selectedCampaignIds.length,
    campaignCount,
    excludeCurrentCampaign,
    currentCampaignMatches,
  ]);

  const isSaveDisabled = effectiveCount > 500 || effectiveCount === 0;

  // Handle filters changed from BenchmarkDataTable
  const handleFiltersChange = (newFilters: Record<string, unknown>) => {
    setTableFilters(newFilters);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      // Initialize with initial filters on open, reset on close
      setTableFilters(initialFilters || {});
      setCampaignCount(0);
      setExcludeCurrentCampaign(initialExcludeCurrentCampaign);
      setCohortMode(initialMode || "static");
      // Create a stable reference to avoid unnecessary re-renders
      const campaignIdsToSet = initialSelectedCampaignIds || [];
      setSelectedCampaignIds(campaignIdsToSet);
      // Reset the currentCampaignMatches state
      setCurrentCampaignMatches(false);
    }
    // Creating a stable reference for campaignIds in the function body
  }, [
    open,
    initialFilters,
    initialExcludeCurrentCampaign,
    initialMode,
    initialSelectedCampaignIds,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent closing the modal during save operations
        if (!newOpen && (isLoading || isClearing || isSaving.current)) {
          return;
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="sm:max-w-[80vw] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Configure Campaign Benchmark Cohort</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="mb-4">
            <div className="mb-4">
              <DialogDescription className="text-muted-foreground">
                Select a cohort of campaigns to use as a benchmark for this
                campaign.
              </DialogDescription>
            </div>

            <div className="mb-2">
              <div className="flex items-center mb-2">
                <h3 className="text-sm font-medium">Cohort mode</h3>
                <InfoTooltip
                  trigger={
                    <button className="text-muted-foreground hover:text-foreground transition-colors ml-1.5">
                      <InfoCircledIcon className="h-4 w-4" />
                    </button>
                  }
                  side="bottom"
                  className="w-[300px]"
                >
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-2">
                      <strong>Dynamic Cohort:</strong> Use filters to define a
                      set of campaigns with similar characteristics. The
                      benchmark will automatically update as campaigns are added
                      or modified.
                    </p>
                    <p className="text-muted-foreground">
                      <strong>Static Cohort:</strong> Hand-pick specific
                      campaigns to include in your benchmark. The benchmark will
                      only include the campaigns you explicitly select.
                    </p>
                  </div>
                </InfoTooltip>
              </div>

              <div className="flex items-center">
                <Select
                  value={cohortMode}
                  onValueChange={(value) =>
                    setCohortMode(value as BenchmarkCohortMode)
                  }
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Cohort mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dynamic">Dynamic</SelectItem>
                    <SelectItem value="static">Static</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground ml-4">
                  {cohortMode === "static"
                    ? "Select specific campaigns to include in your benchmark cohort."
                    : "Use the filters below to narrow down to campaigns with similar characteristics."}
                </p>
              </div>
            </div>
          </div>

          <BenchmarkDataTable
            columns={columns as UnifiedTableColumn<unknown>[]}
            onCountChange={setCampaignCount}
            onFiltersChange={handleFiltersChange}
            tableFilters={initialFilters}
            currentCampaignId={selectedCampaignId}
            // Only include exclude toggle properties in dynamic mode
            {...(cohortMode === "dynamic"
              ? {
                  excludeCurrentCampaign: excludeCurrentCampaign,
                  onExcludeCurrentCampaignChange: setExcludeCurrentCampaign,
                  onCurrentCampaignMatchesChange: setCurrentCampaignMatches,
                }
              : {})}
            currentCampaignName={
              currentCampaign?.results?.[0]?.name || "current campaign"
            }
            // Only include selection mode properties in static mode
            {...(cohortMode === "static"
              ? {
                  selectionMode: true,
                  selectedCampaignIds: selectedCampaignIds,
                  onSelectionChange: setSelectedCampaignIds,
                }
              : {})}
          />
        </div>
        <DialogFooter className="mt-4">
          <div className="mr-auto">
            <CohortCountBadge
              effectiveCount={effectiveCount}
              cohortMode={cohortMode}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading || isClearing || isSaving.current}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={isLoading || isClearing || isSaving.current}
            >
              {isClearing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Clear Benchmark Cohort
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleSave}
                  disabled={
                    isSaveDisabled ||
                    isLoading ||
                    isClearing ||
                    isSaving.current
                  }
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Save Benchmark Cohort
                </Button>
              </TooltipTrigger>
              {isSaveDisabled && (
                <TooltipContent>
                  {campaignCount === 0
                    ? "Please select at least one campaign"
                    : "Please refine your filters to select 500 or fewer campaigns"}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
